import React from "react";

import LoaderSpinner from "@components-shared/LoaderSpinner";
import { BankIdPendingLoader } from "@components/loader/mobile";
import { PopupName } from "@components/popups/shared/PopupName";
import {
	ChatBetSlipSharedPopup,
	SupportChatInfoPopup,
} from "@features/chat/mobile";
import { UserDataConfirmAuthPopup } from "@features/membership/user-data-confirm/shared";
import {
	BetBuilderPicksNotAvailablePopup,
	BetBuilderPicksPopup,
} from "@features/quick-play/mobile/bet-builder-picks";
import BettingTipsPopup from "@features/quick-play/mobile/betting-tips/BettingTipsPopup";
import MostPlayTicketsPopup from "@features/quick-play/mobile/most-played-tickets/MostPlayTicketsPopup";
import SharedBetSlipPopup from "@features/quick-play/mobile/shared-bet-slips/components/SharedBetSlipPopup";
import { useRootAppStore } from "@hooks";
import { ReactLazyImportWithGuard as lazy } from "@lib/lazy-import-with-guard";
import { Modal } from "@lib/modal";
import BlockPopupService from "@services/user/BlockPopupService";
import { BankIdAuthType } from "@data-types/user/BankIdAuthType";
import { MobilePlatform } from "@state/stores/types";
//#region lazy

import { getCurrentCulture } from "@utils";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import { observer } from "mobx-react-lite";

const loadFailPath = `/${getCurrentCulture()}/app-update`;

// prettier-ignore
const RealityCheckPopup = lazy(loadFailPath, ()=>import("./reality-check/RealityCheckPopup"))
// prettier-ignore
const AutomaticLogoutPopup = lazy(loadFailPath, ()=>import("./AutomaticLogoutPopup"))
// prettier-ignore
const User24hLockPopup = lazy(loadFailPath, ()=>import("./User24hLockPopup"))
// prettier-ignore
const AccountVerificationPopup = lazy(loadFailPath, ()=>import("./AccountVerificationPopup"));
// prettier-ignore
const WelcomeLoginPopup = lazy(loadFailPath,() => import("./WelcomeLoginPopup"));
//prettier-ignore
const FailedLoginsPopup = lazy(loadFailPath, () => import("./FailedLoginsPopup"));
//prettier-ignore
const LiveSessionExpirePopup = lazy(loadFailPath, () => import("./LiveSessionExpirePopup"));
//prettier-ignore
const RequiredUserActionsPopup = lazy(loadFailPath, () => import("./RequiredUserActionsPopup"))
//prettier-ignore
const AppPopup = lazy(loadFailPath, () => import("./AppPopup"));
//prettier-ignore
const FavoritePopup = lazy(loadFailPath, () => import("./FavoritePopup"));
//prettier-ignore
const BetBuilderPopup = lazy(loadFailPath, () => import("./BetBuilderPopup"));
//prettier-ignore
const BankIdQrCodePopup = lazy(loadFailPath, () => import("./BankIdQrCodePopup"));
//prettier-ignore
const CountryChangePopup = lazy(loadFailPath, () => import("./CountryChangePopup"));
//prettier-ignore
const OnboardingNewsletterPopup = lazy(loadFailPath, () => import("./OnboardingNewsletterPopup"));
//prettier-ignore
const OnboardingRegisterPopup = lazy(loadFailPath, () => import("./OnboardingRegisterPopup"));
//prettier-ignore
const RestrictionTypeDenyPopup = lazy(loadFailPath, () => import("./RestrictionTypeDenyPopup"));
//prettier-ignore
const BankIdFlowSelectPopup = lazy(loadFailPath, () => import("./BankIdFlowSelectPopup"));
//prettier-ignore
const UseMarketplaceBonusConfirmPopup = lazy(loadFailPath, () => import("./UseMarketplaceBonusConfirmPopup"));
//prettier-ignore
const OnboardingNextPopup = lazy(loadFailPath, () => import("./OnboardingNextPopup"));
//prettier-ignore
const DenyUserMatchingConfirmationPopup = lazy(loadFailPath, () => import("./DenyUserMatchingConfirmationPopup"));
//prettier-ignore
const LogoutPopup = lazy(loadFailPath, () => import("../shared/LogoutPopup"));
//prettier-ignore
const GracePeriodConfirmationPopup = lazy(loadFailPath, () => import("../shared/GracePeriodConfirmationPopup"));
//prettier-ignore
const AreaChangeConfirmationPopup = lazy(loadFailPath, () => import("../shared/AreaChangeConfirmationPopup"));
// prettier-ignore
const SavedSlipBarcodePopup = lazy(loadFailPath, () => import("@features/saved-slip/mobile/components/SavedSlipBarcodePopup"));
// prettier-ignore
const SavedSlipDetailsPopup = lazy(loadFailPath, () => import("@features/saved-slip/mobile/components/SavedSlipDetailsPopup"));
//prettier-ignore
const WalletGracePeriodDepositPopup = lazy(loadFailPath, () => import("../shared/WalletGracePeriodDepositPopup"));

//#endregion lazy

const srOnlyStyle: React.CSSProperties = {
	position: "absolute",
	width: 1,
	height: 1,
	padding: 0,
	margin: -1,
	overflow: "hidden",
	clip: "rect(0,0,0,0)",
	whiteSpace: "nowrap",
	border: 0,
};

// Persistent aria-live announcer — stays mounted outside popups so screen readers
// hear error messages even after a popup has closed.
const BankIdAnnouncer = observer(function BankIdAnnouncer() {
	const { bankIdCapacitorStore } = useRootAppStore<MobilePlatform>();
	return (
		<div aria-live="assertive" aria-atomic="true" style={srOnlyStyle}>
			{bankIdCapacitorStore.srErrorAnnouncement}
		</div>
	);
});

const BankIdStepUpPendingLoader = observer(
	function BankIdStepUpPendingLoader() {
		const { bankIdCapacitorStore } = useRootAppStore<MobilePlatform>();

		if (
			!bankIdCapacitorStore.isAuthPending ||
			bankIdCapacitorStore.authType !== BankIdAuthType.StepUp
		) {
			return null;
		}

		return <LoaderSpinner isLoading isMobile />;
	}
);

export default observer(function PopupDisplay() {
	return (
		<React.Suspense fallback={null}>
			{isCapacitorPlatform() && WEBPACK_IS_BANKID_ENABLED && (
				<BankIdAnnouncer />
			)}
			{isCapacitorPlatform() && WEBPACK_IS_BANKID_ENABLED && (
				<BankIdStepUpPendingLoader />
			)}
			<Modal>
				<Popup />
			</Modal>
		</React.Suspense>
	);
});

const Popup = observer(() => {
	const rootStore = useRootAppStore<MobilePlatform>();
	const {
		isRestrictionTypeDeny,
		automaticLogoutStore: { isActive: isAutomaticLogoutActive },
		realityCheckStore: { isActive: isRealityCheckActive },
		user24hLockStore: { isActive: isUser24hLockActive },
		accountVerificationStore: { isActive: isAccountVerificationActive },
		welcomeStore: {
			isActive: isWelcomePopupActive,
			isUseBonusConfirmActive,
			hideUseBonusConfirmationPopup,
		},
		failedLoginsCheckPopupStore: { isActive: isFailedLoginsPopupActive },
		onboardingWelcomePopupStore: {
			onboardingNewsletterStore: {
				isActive: isOnboardingNewsletterPopupActive,
			},
			onboardingRegistrationStore: {
				isActive: isOnboardingRegisterPopupActive,
			},
			onboardingNextStore: { isActive: isOnboardingNextPopupActive },
		},
		requiredUserActionStore: { isActive: RequiredUserActionsPopupActive },
		offerHub: { isSessionExpired: isOfferHubSessionExpire },
		cashoutHubStore: { isSessionExpired: isCashOutHubSessionExpire },
		userHubStore: { isSessionExpired: isUserHubSessionExpire },
		capacitorUpdaterStore: {
			isLoading: isCapacitorUpdateDownloading,
			isPopupVisible: isCapacitorUpdateActive,
		},
		favoritePopupStore: { isActive: isFavoritePopupActive },
		quickPlayStore: {
			mostPlayedTicketsStore: {
				isPopupDisplayed: isQuickPlayPopupActive,
			},
			sharedBetSlipStore: {
				isPopupDisplayed: isSharedBetSlipPopupActive,
			},
			betBuilderPicksStore: {
				isPopupDisplayed: isBetBoosterPresetPopupActive,
				BBPickNotAvailablePopup: isBBPickNoAvailablePopupActive,
			},
			bettingTipsStore: { isPopupDisplayed: isBettingTipsActive },
		},
		cookieConsentStore: {
			shouldBlockOtherPopups: isCookieConsentPopupActive,
		},
		appPopupStore: { isAppPopupVisible },
		chatViewStore: {
			chatBetSlipShareData: isChatBetSlipSharePopupActive,
			supportChatInfoPopup,
		},
		userAuthStore,
		sportStatisticsHub: {
			isSessionExpired: isSportStatisticsHubSessionExpired,
		},
		betBuilderPopupStore: { isActive: isBetBuilderPopupActive },
		countryChangeStore: {
			isCountryChangePopupActive: isCountryChangePopupActive,
		},
		bankIdCapacitorStore: {
			isBankIdFlowPopupActive: isBankIdFlowPopupActive,
			isBankIdQrCodePopupActive: isBankIdQrCodePopupActive,
		},
		gracePeriodStore: { isActive: isGracePeriodConfirmationActive },
		areaChangeStore: { isActive: isAreaChangeConfirmationActive },
		walletGracePeriodPopupStore: {
			isActive: isWalletGracePeriodPopupActive,
		},
		logoutPopupStore: { isLogoutPopupActive },
		betSlipStore: {
			savedSlipStore: {
				isBarcodePopupActive: isBarcodePopupActive,
				isReadyBetTicketDetailsPopupActive:
					isReadyBetTicketDetailsPopupActive,
				savedSlip: savedSlip,
			},
		},
	} = rootStore;

	if (
		(isAutomaticLogoutActive ||
			isRealityCheckActive ||
			isUser24hLockActive ||
			isAccountVerificationActive ||
			isWelcomePopupActive ||
			isFailedLoginsPopupActive ||
			isOnboardingNewsletterPopupActive ||
			isOnboardingRegisterPopupActive ||
			RequiredUserActionsPopupActive ||
			isOfferHubSessionExpire ||
			isCashOutHubSessionExpire ||
			isCapacitorUpdateDownloading ||
			isCapacitorUpdateActive ||
			isFavoritePopupActive ||
			isQuickPlayPopupActive ||
			isBBPickNoAvailablePopupActive ||
			isBetBoosterPresetPopupActive ||
			isSharedBetSlipPopupActive ||
			isBettingTipsActive ||
			isCookieConsentPopupActive) &&
		rootStore.chatViewStore.chatBoxVisible
	) {
		rootStore.chatViewStore.toggleChatBox(false);
	}

	if (
		isCapacitorPlatform() &&
		(isCapacitorUpdateActive || isCapacitorUpdateDownloading)
	) {
		//we dont want to show any popups if popup for updating needs to be shown or downloading loader
		return null;
	}

	if (isRestrictionTypeDeny && isCapacitorPlatform())
		return <RestrictionTypeDenyPopup />;

	if (isCookieConsentPopupActive) {
		//do not render any popups if consent is shown
		return null;
	}

	if (isLogoutPopupActive) {
		return <LogoutPopup />;
	}

	if (
		isRealityCheckActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.RealityCheck)
	) {
		return (
			<RealityCheckPopup isRealityCheckActive={isRealityCheckActive} />
		);
	}

	if (
		isAutomaticLogoutActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.AutomaticLogout)
	) {
		return <AutomaticLogoutPopup isOpened={isAutomaticLogoutActive} />;
	}
	if (
		(isOfferHubSessionExpire ||
			isCashOutHubSessionExpire ||
			isSportStatisticsHubSessionExpired ||
			isUserHubSessionExpire) &&
		!BlockPopupService.checkIfPopupBlocked(
			PopupName.SessionExpire || PopupName.CashoutExpire
		)
	) {
		return <LiveSessionExpirePopup />;
	}

	if (isBBPickNoAvailablePopupActive) {
		return <BetBuilderPicksNotAvailablePopup />;
	}

	if (
		//check
		isBetBuilderPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.BetBuilder)
	) {
		return <BetBuilderPopup />;
	}
	if (
		isQuickPlayPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.QuickPlay)
	) {
		return <MostPlayTicketsPopup />;
	}

	if (isBetBoosterPresetPopupActive) {
		return <BetBuilderPicksPopup />;
	}

	if (isBarcodePopupActive) {
		return <SavedSlipBarcodePopup />;
	}

	if (isReadyBetTicketDetailsPopupActive) {
		return <SavedSlipDetailsPopup savedSlip={savedSlip} />;
	}

	if (
		isSharedBetSlipPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.SharedBetSlip)
	) {
		return <SharedBetSlipPopup />;
	}

	if (
		isBettingTipsActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.BettingTips)
	) {
		return (
			<BettingTipsPopup
				bettingTipsStore={rootStore.quickPlayStore.bettingTipsStore}
			/>
		);
	}

	if (isAppPopupVisible) {
		return <AppPopup />;
	}

	if (
		isAccountVerificationActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.AccountVerification)
	) {
		return (
			<AccountVerificationPopup isOpened={isAccountVerificationActive} />
		);
	}

	if (
		isUser24hLockActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.User24LockActive)
	) {
		return <User24hLockPopup />;
	}

	if (
		isWelcomePopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.WelcomePopup)
	) {
		return <WelcomeLoginPopup />;
	}

	if (
		isUseBonusConfirmActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.UseBonus)
	) {
		return (
			<UseMarketplaceBonusConfirmPopup
				hidePopup={hideUseBonusConfirmationPopup}
				sendConfirmation={(value: boolean) =>
					userAuthStore.sendWelcomeUseBonusConfirmation(value)
				}
			/>
		);
	}

	if (
		isFailedLoginsPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.FailedLogins)
	) {
		return <FailedLoginsPopup />;
	}

	if (
		isFavoritePopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.FavoritePopup)
	) {
		return <FavoritePopup />;
	}

	if (
		isOnboardingNextPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.OnboardingNext)
	) {
		return (
			<OnboardingNextPopup
				isOnboardingNextPopupActive={isOnboardingNextPopupActive}
			/>
		);
	}

	if (
		isOnboardingRegisterPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.OnboardingRegister)
	) {
		return (
			<OnboardingRegisterPopup
				isOnboardingRegisterPopupActive={
					isOnboardingRegisterPopupActive
				}
			/>
		);
	}

	if (
		isOnboardingNewsletterPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.OnboardingNewsletter)
	) {
		return (
			<OnboardingNewsletterPopup
				isOnboardingNewsletterPopupActive={
					isOnboardingNewsletterPopupActive
				}
			/>
		);
	}

	if (
		RequiredUserActionsPopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.RequiredUserActions)
	) {
		if (userAuthStore.user) {
			return <RequiredUserActionsPopup />;
		}
	}

	if (
		isChatBetSlipSharePopupActive &&
		!BlockPopupService.checkIfPopupBlocked(PopupName.ChatBetSlipShare)
	) {
		return <ChatBetSlipSharedPopup />;
	}

	if (isCountryChangePopupActive) {
		return <CountryChangePopup />;
	}

	if (supportChatInfoPopup) {
		return <SupportChatInfoPopup />;
	}

	if (
		isCapacitorPlatform() &&
		isBankIdFlowPopupActive &&
		WEBPACK_IS_BANKID_ENABLED
	) {
		return <BankIdFlowSelectPopup />;
	}

	if (
		isCapacitorPlatform() &&
		isBankIdQrCodePopupActive &&
		WEBPACK_IS_BANKID_ENABLED
	) {
		return <BankIdQrCodePopup />;
	}

	if (isWalletGracePeriodPopupActive) {
		return <WalletGracePeriodDepositPopup />;
	}

	if (isGracePeriodConfirmationActive) {
		return <GracePeriodConfirmationPopup />;
	}

	if (isAreaChangeConfirmationActive) {
		return <AreaChangeConfirmationPopup />;
	}

	if (
		userAuthStore.userDataConfirmationStore
			.isUserDataConfirmationPopupActive
	) {
		return <UserDataConfirmAuthPopup />;
	}

	if (userAuthStore.userMatchingStore.isDenyConfirmationPopupActive) {
		return <DenyUserMatchingConfirmationPopup />;
	}

	return null;
});
